import { useRouter } from 'next/compat/router'
import Link from 'next/link'
import { useSelector } from 'react-redux'

import {
  Avatar,
  Box,
  PopoverTrigger as ChakraPopoverTrigger,
  HStack,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  Square,
  Text,
} from '@chakra-ui/react'
import styled from '@emotion/styled'

import { selectSideNavOpen } from '@/redux/ui/uiSlice'
import { SIDE_BAR_WIDTH_CLOSED, SIDE_BAR_WIDTH_OPEN } from '@/styles/theme'

const PopoverTrigger = ChakraPopoverTrigger as React.FC<{
  children: React.ReactNode
}>

/**
 * Styled component for sidebar navigation items.
 * Uses transient props ($) to prevent custom props from being forwarded to the DOM.
 * Renders as Link (via `as` prop) for navigation items, or div for non-navigation items.
 */
const StyledSideBarNavItem = styled.div<{
  $isExpanded: boolean
  $isActive: boolean
  href?: string
}>`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  transition: ease-in-out 0.3s width;
  width: ${(p) =>
    p.$isExpanded ? SIDE_BAR_WIDTH_OPEN : SIDE_BAR_WIDTH_CLOSED};
  background: ${(p) =>
    p.$isActive
      ? 'linear-gradient(158.67deg, #6096f5 100%, #3379c7 0%)'
      : 'transparent'};

  .chakra-icon {
    color: ${(p) => (p.$isActive ? 'white' : '#5a6c9a')};
    transition: 0.3s;
  }

  &:hover {
    background-color: #1b253f;
  }
`

const StyledAvatar = styled(Avatar)`
  .chakra-avatar__initials {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.5px;
    line-height: 19px;
  }
`

interface IProps {
  label: string
  avatarName?: string
  subLabel?: string
  icon?: React.ElementType
  iconSize?: string
  onClick?: () => void
  path?: string
  dataTestId?: string
  badge?: React.ReactNode
  // See list of options: https://developer.mozilla.org/en-US/docs/Web/CSS/cursor
  cursor?: string
}

export const SideBarNavItem = ({
  label,
  avatarName,
  subLabel,
  icon,
  iconSize,
  onClick,
  path,
  dataTestId,
  badge,
  cursor = 'pointer',
}: IProps) => {
  const { pathname } = useRouter()
  const isSideNavOpen = useSelector(selectSideNavOpen)
  const isActive = pathname === path

  const content = (
    <>
      <Popover
        isOpen={isSideNavOpen ? false : undefined}
        placement='right'
        trigger='hover'
      >
        <PopoverTrigger>
          <Square
            cursor={cursor}
            h='60px'
            position='relative'
            w={SIDE_BAR_WIDTH_CLOSED}
          >
            {badge && !isSideNavOpen && (
              <Box
                bg='signalyellow-400'
                borderRadius='full'
                h='8px'
                position='absolute'
                right='10px'
                top='12px'
                w='8px'
              />
            )}
            {avatarName ? (
              <StyledAvatar
                bgGradient='linear-gradient(180deg, #50647A 0%, #3F609C 100%)'
                boxShadow='0 4px 8px 0 rgba(44,133,244,0.26)'
                color='#fff'
                fontSize='15px'
                h='42px'
                letterSpacing='-0.5px'
                name={avatarName}
                w='42px'
              />
            ) : (
              <Icon
                as={icon}
                boxSize={iconSize ? iconSize : '24px'}
                strokeColor={isActive ? '#fff' : '#5a6c9a'}
              />
            )}
          </Square>
        </PopoverTrigger>
        <PopoverContent
          bg={
            isActive
              ? 'linear-gradient(158.67deg, #6096f5 100%, #3379c7 0%)'
              : '#1b253f'
          }
          border='none'
          borderRadius='5px'
          color='#fff'
          w='auto'
        >
          <PopoverBody
            fontSize='16px'
            fontWeight='semibold'
            letterSpacing='-0.65px'
            p='2'
          >
            <HStack>
              <>
                <Text>{label}</Text>
                {badge}
              </>
            </HStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      {isSideNavOpen && (
        <Box>
          {subLabel && (
            <Box
              color='#fff'
              fontSize='10px'
              letterSpacing='-0.4px'
              opacity={0.4}
            >
              {subLabel}
            </Box>
          )}

          {isSideNavOpen && (
            <Box alignItems='center' display='flex' gap='8px'>
              <Box
                color='#fff'
                fontSize='16px'
                fontWeight='medium'
                letterSpacing='-0.65px'
                noOfLines={1}
              >
                {label}
              </Box>
              {badge}
            </Box>
          )}
        </Box>
      )}
    </>
  )

  return (
    <StyledSideBarNavItem
      $isActive={isActive}
      $isExpanded={isSideNavOpen}
      as={path ? Link : undefined}
      data-testid={dataTestId}
      href={path}
      onClick={onClick}
    >
      {content}
    </StyledSideBarNavItem>
  )
}
